/**
 * TenantAiConfigPage — Sub-tela 3 IA do Brief 5 §7.5 (Fase 2 multi-modelo).
 *
 * Layout PER-AGENTE (decisão 27/Mai/2026 — §7.5 tenant-wide tratado como
 * superseded para este escopo): admin do tenant aponta o modelo efetivo de
 * cada agente nominal overridable. Grava em `ai_agents` per-tenant; o
 * `resolveEffectiveModel` (Fase 1) já lê dali.
 *
 * Capability: `custom-model-selection` (Professional+ / admin_tenant — ADR-123).
 * Stubs (routing/tool) ocultos pelo backend; protection-agent editável
 * (modelOverridable=true) com aviso forte se modelo não-Anthropic for escolhido.
 *
 * Task 6: toggle de failover + editor de cadeia-reserva por agente.
 *
 * Admin Hub T8 — o miolo (antes `TenantAiConfigBody`, privado) foi renomeado e
 * exportado como `ModelosSection`: é o que a página de domínio `IaCondutaPage`
 * (`hub/domains/IaCondutaPage.tsx`) compõe sob o item de catálogo `modelos`
 * (capability `custom-model-selection`, `tierLabel: 'Pro'`). `TenantAiConfigPage`
 * (esta função, o gate de LEITURA + `<h1>`) continua existindo só para a rota
 * legada `/admin/ai-config` — que hoje é puro redirect para `/admin/ia#modelos`
 * (T3) — e para o próprio `TenantAiConfigPage.spec.tsx`, que a testa direto.
 *
 * Um decisor por eixo: dentro de `IaCondutaPage`, o gate de LEITURA é o da
 * `DomainSection` (catálogo) — `ModelosSection` NÃO tem gate de leitura próprio,
 * então usá-la fora de um `DomainSection`/`TenantAiConfigPage` deixaria o
 * conteúdo aberto. O gate de ESCRITA (`canManage`, linha ~99 abaixo) é
 * `useCapability('custom-model-selection')` resolvido DENTRO de `ModelosSection`
 * — não veio como prop (diferente do padrão dos 9 tabs de `config-tenant/`,
 * que recebem `canManage: tenant-config-manage` via prop): aqui a capability de
 * escrita é a MESMA do gate de leitura do catálogo, então recalculá-la
 * localmente é o padrão já usado por `BrandingSection` (T7) para o mesmo caso
 * (write gate tier-based, não o `tenant-config-manage` genérico).
 *
 * `ModelosSection` NÃO se registra na `StickySaveBar`: não há botão Salvar nem
 * `isDirty` nesta tela — cada linha/ação grava por si (`setModel.mutate`,
 * `setFailover.mutate`, `applyProfile.mutate`, etc., todos imediatos). Não há
 * "sujo" para agregar — confirmado lendo o corpo inteiro antes de mover.
 */

import { useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';

import {
  useAiAgents,
  useAiProfiles,
  useApplyAiProfile,
  useAvailableModels,
  useClearAgentModel,
  useDynamismScale,
  useFailoverEnabled,
  useSetAgentActivationMode,
  useSetAgentFallback,
  useSetAgentModel,
  useSetAgentReasoning,
  useSetDynamismScale,
  useSetFailoverEnabled,
} from './use-ai-config';

import type {
  ActivationMode,
  AgentModelConfigWire,
  DynamismScaleWire,
  ModelOptionWire,
  ProviderModelsWire,
  ReasoningEffort,
} from './types';

const PROTECTION_AGENT_ID = 'protection-agent';

/** Faixas da escala de dinamismo (fast-follow) — vocabulário dos perfis de IA. */
const DYNAMISM_OPTIONS: readonly { id: DynamismScaleWire; label: string; description: string }[] = [
  {
    id: 'economico',
    label: 'Econômico',
    description: 'Análise comportamental só em risco forte (Nível crítico / hostilidade).',
  },
  {
    id: 'equilibrado',
    label: 'Equilibrado',
    description: 'Cobertura conservadora padrão (Nível ≥3, deriva, sentimento negativo).',
  },
  {
    id: 'cobertura_total',
    label: 'Cobertura total',
    description: 'Roda em quase todo turno não-calmo (máxima cobertura).',
  },
];

/** Retorna true se o modelo NÃO é Claude (Anthropic). */
function isNonAnthropic(model: string): boolean {
  return model.trim() !== '' && !model.startsWith('claude-');
}

export function TenantAiConfigPage(): JSX.Element {
  const canManage = useCapability('custom-model-selection');

  if (!canManage) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="rounded-2xl border border-border-subtle bg-surface p-8 max-w-md">
          <span className="text-5xl">🤖</span>
          <h2 className="mt-4 text-lg font-semibold text-text-primary">Configuração de IA</h2>
          <p className="mt-2 text-sm text-text-secondary">
            Esta funcionalidade requer o pacote <strong>Professional</strong> ou superior.
          </p>
          <p className="mt-1 text-xs text-text-secondary">
            Fale com o administrador do tenant para habilitar a seleção de modelos de IA.
          </p>
          <button
            type="button"
            className="mt-6 rounded bg-indigo-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Ver planos disponíveis
          </button>
        </div>
      </div>
    );
  }

  return <ModelosSection />;
}

export function ModelosSection(): JSX.Element {
  const canManage = useCapability('custom-model-selection');
  const agents = useAiAgents(canManage);
  const models = useAvailableModels(canManage);
  const failover = useFailoverEnabled(canManage);
  const profiles = useAiProfiles(canManage);
  const setModel = useSetAgentModel();
  const clearModel = useClearAgentModel();
  const setFailover = useSetFailoverEnabled();
  const setFallback = useSetAgentFallback();
  const applyProfile = useApplyAiProfile();
  const setActivationMode = useSetAgentActivationMode();
  const setReasoning = useSetAgentReasoning();
  const dynamismScale = useDynamismScale(canManage);
  const setDynamism = useSetDynamismScale();

  const failoverOn = failover.data?.enabled ?? false;
  const currentScale = dynamismScale.data ?? 'equilibrado';

  return (
    <div className="flex h-full flex-col">
      {/* `<h1>Configuração de IA</h1>` saiu daqui (Admin Hub T8) — quem compõe
          esta seção dá o título (o `<h2>` de `DomainSection` via `item.label`,
          ou o próprio `<h1>` de `TenantAiConfigPage` acima). O parágrafo FICOU:
          carrega informação (Ollama on-premise vs. custo por tier) que não está
          duplicada no `item.efeito` do catálogo ("Qual modelo de IA atende cada
          agente e o que assume quando ele falha."). */}
      <div className="border-b border-border-subtle bg-surface px-8 py-5">
        <p className="text-sm text-text-secondary">
          Escolha o modelo de cada agente nominal para o seu tenant. Modelos locais (Ollama) rodam
          on-premise sem custo por token; Claude via API tem custo conforme tier.
        </p>
      </div>

      {/* ── Failover toggle ───────────────────────────────────────────── */}
      <div className="border-b border-border-subtle bg-surface px-8 py-4">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-text-primary">Failover de modelos</p>
            <p className="mt-0.5 text-xs text-text-secondary">
              Quando o modelo primário falhar por indisponibilidade (sem crédito, limite, fora do
              ar), usar a reserva automaticamente.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={failoverOn}
            disabled={setFailover.isPending || failover.isLoading}
            onClick={() => setFailover.mutate(!failoverOn)}
            className={`relative mt-0.5 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 ${
              failoverOn ? 'bg-indigo-600' : 'bg-gray-200'
            }`}
            aria-label="Failover de modelos"
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform ${
                failoverOn ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* ── Escala de dinamismo (gate dinâmico do behavioral-agent) ───── */}
      <div className="border-b border-border-subtle bg-surface px-8 py-4">
        <p className="text-sm font-medium text-text-primary">Escala de dinamismo</p>
        <p className="mt-0.5 text-xs text-text-secondary">
          Quando a análise comportamental está em modo dinâmico, define o quão agressivamente ela
          roda por turno. Regulado e turnos sem sinal sempre disparam (piso de segurança).
        </p>
        <div
          className="mt-3 inline-flex rounded-md border border-border-subtle p-0.5"
          role="radiogroup"
          aria-label="Escala de dinamismo"
        >
          {DYNAMISM_OPTIONS.map((opt) => {
            const active = currentScale === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={active}
                title={opt.description}
                disabled={setDynamism.isPending || dynamismScale.isLoading}
                onClick={() => setDynamism.mutate(opt.id)}
                className={`rounded px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                  active ? 'bg-indigo-600 text-white' : 'text-text-secondary hover:bg-surface-hover'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-6 space-y-8">
        {/* ── Modelos por agente ────────────────────────────────────── */}
        {agents.isLoading ? (
          <p className="text-sm text-text-secondary">Carregando agentes…</p>
        ) : agents.isError || agents.data === undefined ? (
          <p className="text-sm text-red-600">Falha ao carregar configuração de IA.</p>
        ) : (
          <div className="space-y-3">
            {agents.data.map((agent) => (
              <AgentModelRow
                key={agent.agentId}
                agent={agent}
                providers={models.data ?? []}
                modelsLoading={models.isLoading}
                failoverOn={failoverOn}
                onSetModel={(model) => setModel.mutate({ agentId: agent.agentId, model })}
                onClear={() => clearModel.mutate(agent.agentId)}
                onSetFallback={(fallbackModels) =>
                  setFallback.mutate({ agentId: agent.agentId, models: fallbackModels })
                }
                onSetReasoning={(effort) => setReasoning.mutate({ agentId: agent.agentId, effort })}
                isSaving={
                  setModel.isPending ||
                  clearModel.isPending ||
                  setFallback.isPending ||
                  setReasoning.isPending
                }
              />
            ))}
          </div>
        )}

        {/* ── Modos de operação da IA ───────────────────────────────── */}
        {agents.data !== undefined && (
          <AiOperationModesSection
            agents={agents.data}
            profiles={profiles.data ?? []}
            profilesLoading={profiles.isLoading}
            isSaving={applyProfile.isPending || setActivationMode.isPending}
            onApplyProfile={(profile) => applyProfile.mutate({ profile })}
            onSetActivationMode={(agentId, mode) => setActivationMode.mutate({ agentId, mode })}
          />
        )}
      </div>
    </div>
  );
}

interface AgentModelRowProps {
  readonly agent: AgentModelConfigWire;
  readonly providers: readonly ProviderModelsWire[];
  readonly modelsLoading: boolean;
  readonly failoverOn: boolean;
  readonly onSetModel: (model: string) => void;
  readonly onClear: () => void;
  readonly onSetFallback: (models: string[]) => void;
  readonly onSetReasoning: (effort: ReasoningEffort | null) => void;
  readonly isSaving: boolean;
}

function AgentModelRow({
  agent,
  providers,
  modelsLoading,
  failoverOn,
  onSetModel,
  onClear,
  onSetFallback,
  onSetReasoning,
  isSaving,
}: AgentModelRowProps): JSX.Element {
  const isProtection = agent.agentId === PROTECTION_AGENT_ID;

  return (
    <div className="flex items-start gap-4 rounded-xl border border-border-subtle bg-surface p-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <code className="font-mono text-sm font-semibold text-text-primary">{agent.agentId}</code>
          <CostBadge category={agent.costCategory} />
          {isProtection && <AnthropicRecommendedBadge />}
        </div>
        <p className="mt-1 text-xs text-text-secondary">{agent.description}</p>
        <p className="mt-2 text-xs text-text-secondary">
          Default do catálogo:{' '}
          <code className="font-mono text-text-primary">{agent.catalogDefault}</code>
        </p>

        {/* Cadeia de reserva (quando failover ON) */}
        {failoverOn && agent.fallbackModels.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1 text-xs text-text-secondary">
            <span className="font-medium text-text-primary">{agent.effectiveModel}</span>
            {agent.fallbackModels.map((m) => (
              <span key={m} className="flex items-center gap-1">
                <span aria-hidden>→</span>
                <span className="font-mono text-text-primary">{m}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-2 min-w-[280px]">
        {agent.modelOverridable ? (
          <>
            <ModelSelectWithWarning
              agentId={agent.agentId}
              effectiveModel={agent.effectiveModel}
              providers={providers}
              loading={modelsLoading}
              disabled={isSaving}
              isProtection={isProtection}
              onChange={onSetModel}
            />
            {agent.overridden && (
              <button
                type="button"
                onClick={onClear}
                disabled={isSaving}
                aria-label={`Resetar ${agent.agentId} para o default`}
                className="text-xs text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
              >
                Resetar para default
              </button>
            )}

            {/* Item 566 — o «pensar», só onde cabe (ou para desligar uma escolha que perdeu o efeito) */}
            {(agent.reasoningAllowed || agent.reasoningEffort !== null) && (
              <ReasoningSelect
                agentId={agent.agentId}
                effort={agent.reasoningEffort}
                allowed={agent.reasoningAllowed}
                disabled={isSaving}
                onChange={onSetReasoning}
              />
            )}

            {/* Editor de cadeia de reserva (quando failover ON) */}
            {failoverOn && (
              <FallbackEditor
                agentId={agent.agentId}
                currentFallback={agent.fallbackModels[0] ?? ''}
                providers={providers}
                loading={modelsLoading}
                disabled={isSaving}
                isProtection={isProtection}
                onSetFallback={onSetFallback}
              />
            )}
          </>
        ) : (
          <div className="flex flex-col items-end gap-1">
            <span className="font-mono text-sm text-text-primary">{agent.effectiveModel}</span>
            <span className="text-xs text-text-secondary">Modelo fixado pela plataforma</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── AiOperationModesSection ───────────────────────────────────────────────────

const ACTIVATION_MODE_LABELS: Record<ActivationMode, string> = {
  automatic: 'automatic — ativado automaticamente',
  dynamic: 'dynamic — ativação contextual',
  on_demand: 'on_demand — o atendente pedirá sob demanda',
  disabled: 'disabled — desativado',
};

/** Microcópia honesta para o modo on_demand. */
const ON_DEMAND_NOTE =
  'o atendente pedirá sob demanda (o botão no atendimento chega na próxima fatia)';

interface AiOperationModesSectionProps {
  readonly agents: readonly AgentModelConfigWire[];
  readonly profiles: readonly { id: string; label: string; description: string }[];
  readonly profilesLoading: boolean;
  readonly isSaving: boolean;
  readonly onApplyProfile: (profile: string) => void;
  readonly onSetActivationMode: (agentId: string, mode: string) => void;
}

function AiOperationModesSection({
  agents,
  profiles,
  profilesLoading,
  isSaving,
  onApplyProfile,
  onSetActivationMode,
}: AiOperationModesSectionProps): JSX.Element {
  return (
    <section aria-labelledby="ai-modes-heading">
      <div className="mb-4">
        <h2 id="ai-modes-heading" className="text-base font-semibold text-text-primary">
          Modos de operação da IA
        </h2>
        <p className="mt-0.5 text-sm text-text-secondary">
          Configure quando cada agente atua e aplique perfis prontos para ajustar o equilíbrio entre
          custo e cobertura.
        </p>
      </div>

      {/* Perfis prontos */}
      {!profilesLoading && profiles.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-2">
          {profiles.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={isSaving}
              onClick={() => onApplyProfile(p.id)}
              title={p.description}
              className="rounded-full border border-border-subtle bg-surface px-4 py-1.5 text-sm font-medium text-text-primary hover:bg-surface-hover disabled:opacity-50"
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* Linhas por agente */}
      <div className="space-y-3">
        {agents.map((agent) => (
          <div
            key={agent.agentId}
            className="flex items-start gap-4 rounded-xl border border-border-subtle bg-surface p-4"
          >
            <div className="flex-1 min-w-0">
              <code className="font-mono text-sm font-semibold text-text-primary">
                {agent.agentId}
              </code>
              <p className="mt-1 text-xs text-text-secondary">{agent.description}</p>
            </div>

            <div className="min-w-[300px]">
              {agent.activationToggleable ? (
                <ActivationModeSelectWithWarning
                  agentId={agent.agentId}
                  currentMode={agent.activationMode}
                  allowedModes={agent.activationModeAllowed}
                  disabled={isSaving}
                  onChange={onSetActivationMode}
                />
              ) : (
                <div className="flex flex-col items-end gap-1">
                  <span className="text-sm font-medium text-text-primary">
                    {agent.activationMode}
                  </span>
                  <span className="text-xs text-text-secondary">sempre ativo — segurança</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── ActivationModeSelectWithWarning ───────────────────────────────────────────

interface ActivationModeSelectWithWarningProps {
  readonly agentId: string;
  readonly currentMode: ActivationMode;
  readonly allowedModes: readonly ActivationMode[];
  readonly disabled: boolean;
  readonly onChange: (agentId: string, mode: string) => void;
}

function ActivationModeSelectWithWarning({
  agentId,
  currentMode,
  allowedModes,
  disabled,
  onChange,
}: ActivationModeSelectWithWarningProps): JSX.Element {
  const [pendingMode, setPendingMode] = useState<ActivationMode | null>(null);

  function handleChange(mode: ActivationMode): void {
    if (mode === 'disabled') {
      setPendingMode(mode);
    } else {
      onChange(agentId, mode);
    }
  }

  function confirm(): void {
    if (pendingMode !== null) {
      onChange(agentId, pendingMode);
      setPendingMode(null);
    }
  }

  function cancel(): void {
    setPendingMode(null);
  }

  const displayMode = pendingMode ?? currentMode;
  const label = `Modo do ${agentId}`;

  return (
    <div className="flex flex-col items-end gap-2 w-full">
      <label className="flex flex-col items-end gap-1 w-full">
        <span className="sr-only">{label}</span>
        <select
          aria-label={label}
          value={displayMode}
          disabled={disabled}
          onChange={(e) => handleChange(e.target.value as ActivationMode)}
          className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary focus:border-indigo-500 focus:outline-none disabled:opacity-50"
        >
          {allowedModes.map((m) => (
            <option key={m} value={m}>
              {ACTIVATION_MODE_LABELS[m] ?? m}
            </option>
          ))}
        </select>
      </label>
      {displayMode === 'on_demand' && pendingMode === null && (
        <p className="text-xs text-text-secondary self-start">{ON_DEMAND_NOTE}</p>
      )}
      {pendingMode !== null && <DisabledModeWarning onConfirm={confirm} onCancel={cancel} />}
    </div>
  );
}

// ── DisabledModeWarning ───────────────────────────────────────────────────────

interface DisabledModeWarningProps {
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

function DisabledModeWarning({ onConfirm, onCancel }: DisabledModeWarningProps): JSX.Element {
  return (
    <div className="w-full rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs">
      <p className="font-medium text-amber-900">
        Recomendamos manter este agente ativo; desativá-lo pode degradar a segurança e a qualidade
        do atendimento.
      </p>
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-2 py-1 text-xs text-amber-800 hover:bg-amber-100"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rounded bg-amber-700 px-2 py-1 text-xs text-white hover:bg-amber-800"
        >
          Confirmar mesmo assim
        </button>
      </div>
    </div>
  );
}

// ── ModelSelectWithWarning ────────────────────────────────────────────────────

interface ModelSelectWithWarningProps {
  readonly agentId: string;
  readonly effectiveModel: string;
  readonly providers: readonly ProviderModelsWire[];
  readonly loading: boolean;
  readonly disabled: boolean;
  readonly isProtection: boolean;
  readonly onChange: (model: string) => void;
}

function ModelSelectWithWarning({
  agentId,
  effectiveModel,
  providers,
  loading,
  disabled,
  isProtection,
  onChange,
}: ModelSelectWithWarningProps): JSX.Element {
  const [pendingModel, setPendingModel] = useState<string | null>(null);

  function handleChange(model: string): void {
    if (isProtection && isNonAnthropic(model)) {
      setPendingModel(model);
    } else {
      onChange(model);
    }
  }

  function confirm(): void {
    if (pendingModel !== null) {
      onChange(pendingModel);
      setPendingModel(null);
    }
  }

  function cancel(): void {
    setPendingModel(null);
  }

  return (
    <div className="flex flex-col items-end gap-2 w-full">
      <ModelSelect
        agentId={agentId}
        effectiveModel={pendingModel ?? effectiveModel}
        providers={providers}
        loading={loading}
        disabled={disabled}
        onChange={handleChange}
      />
      {pendingModel !== null && <ProtectionWarning onConfirm={confirm} onCancel={cancel} />}
    </div>
  );
}

// ── FallbackEditor ────────────────────────────────────────────────────────────

interface FallbackEditorProps {
  readonly agentId: string;
  readonly currentFallback: string;
  readonly providers: readonly ProviderModelsWire[];
  readonly loading: boolean;
  readonly disabled: boolean;
  readonly isProtection: boolean;
  readonly onSetFallback: (models: string[]) => void;
}

function FallbackEditor({
  agentId,
  currentFallback,
  providers,
  loading,
  disabled,
  isProtection,
  onSetFallback,
}: FallbackEditorProps): JSX.Element {
  const [pendingFallback, setPendingFallback] = useState<string | null>(null);

  function handleFallbackChange(model: string): void {
    if (isProtection && isNonAnthropic(model)) {
      setPendingFallback(model);
    } else {
      onSetFallback(model === '' ? [] : [model]);
    }
  }

  function confirm(): void {
    if (pendingFallback !== null) {
      onSetFallback(pendingFallback === '' ? [] : [pendingFallback]);
      setPendingFallback(null);
    }
  }

  function cancel(): void {
    setPendingFallback(null);
  }

  const displayFallback = pendingFallback ?? currentFallback;
  const label = `Reserva do ${agentId}`;

  return (
    <div className="flex flex-col items-end gap-2 w-full">
      <label className="flex flex-col items-end gap-1 w-full">
        <span className="text-xs text-text-secondary self-start">Modelo de reserva</span>
        <select
          aria-label={label}
          value={displayFallback}
          disabled={disabled || loading}
          onChange={(e) => handleFallbackChange(e.target.value)}
          className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm font-mono text-text-primary focus:border-indigo-500 focus:outline-none disabled:opacity-50"
        >
          <option value="">— nenhum —</option>
          {/* Se o fallback atual não está na lista, mantém visível */}
          {displayFallback !== '' && !isModelInProviders(displayFallback, providers) && (
            <option value={displayFallback}>{displayFallback} (não disponível)</option>
          )}
          {providers.map((p) => (
            <optgroup key={p.provider} label={`${p.label}${p.live ? ' · ao vivo' : ''}`}>
              {p.models.map((m: ModelOptionWire) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      {pendingFallback !== null && <ProtectionWarning onConfirm={confirm} onCancel={cancel} />}
    </div>
  );
}

// ── ProtectionWarning ─────────────────────────────────────────────────────────

interface ProtectionWarningProps {
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

function ProtectionWarning({ onConfirm, onCancel }: ProtectionWarningProps): JSX.Element {
  return (
    <div className="w-full rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs">
      <p className="font-medium text-amber-900">
        Recomendamos fortemente modelos Anthropic para o agente de proteção; a qualidade da proteção
        pode degradar com outros provedores.
      </p>
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-2 py-1 text-xs text-amber-800 hover:bg-amber-100"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rounded bg-amber-700 px-2 py-1 text-xs text-white hover:bg-amber-800"
        >
          Confirmar mesmo assim
        </button>
      </div>
    </div>
  );
}

// ── ModelSelect ───────────────────────────────────────────────────────────────

interface ModelSelectProps {
  readonly agentId: string;
  readonly effectiveModel: string;
  readonly providers: readonly ProviderModelsWire[];
  readonly loading: boolean;
  readonly disabled: boolean;
  readonly onChange: (model: string) => void;
}

function ModelSelect({
  agentId,
  effectiveModel,
  providers,
  loading,
  disabled,
  onChange,
}: ModelSelectProps): JSX.Element {
  const label = `Modelo do ${agentId}`;
  return (
    <label className="flex flex-col items-end gap-1 w-full">
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={effectiveModel}
        disabled={disabled || loading}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm font-mono text-text-primary focus:border-indigo-500 focus:outline-none disabled:opacity-50"
      >
        {/* Se o modelo efetivo não está na lista (ex.: Ollama offline + não-allowlisted), mantém visível. */}
        {!isModelInProviders(effectiveModel, providers) && (
          <option value={effectiveModel}>{effectiveModel} (não disponível)</option>
        )}
        {providers.map((p) => (
          <optgroup key={p.provider} label={`${p.label}${p.live ? ' · ao vivo' : ''}`}>
            {p.models.map((m: ModelOptionWire) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}

// ── ReasoningSelect (Item 566) ────────────────────────────────────────────────

/** «Desligado» grava `null` (não escolheu — o padrão do provedor, que no Flash é não pensar). */
const REASONING_OPTIONS: readonly { value: '' | 'low' | 'medium'; label: string }[] = [
  { value: '', label: 'Desligado' },
  { value: 'low', label: 'Leve' },
  { value: 'medium', label: 'Médio' },
];

interface ReasoningSelectProps {
  readonly agentId: string;
  readonly effort: ReasoningEffort | null;
  readonly allowed: boolean;
  readonly disabled: boolean;
  readonly onChange: (effort: ReasoningEffort | null) => void;
}

function ReasoningSelect({
  agentId,
  effort,
  allowed,
  disabled,
  onChange,
}: ReasoningSelectProps): JSX.Element {
  const label = `Pensar antes de responder — ${agentId}`;
  const value = effort === null || effort === 'none' ? '' : effort;
  return (
    <div className="flex w-full flex-col items-end gap-1">
      <label className="flex w-full items-center justify-end gap-2">
        <span className="text-xs text-text-secondary">Pensar antes de responder</span>
        <select
          aria-label={label}
          value={value}
          disabled={disabled}
          onChange={(e) =>
            onChange(e.target.value === '' ? null : (e.target.value as ReasoningEffort))
          }
          className="rounded border border-border-subtle bg-surface px-2 py-1 text-sm text-text-primary focus:border-indigo-500 focus:outline-none disabled:opacity-50"
        >
          {REASONING_OPTIONS.filter((o) => allowed || o.value === '' || o.value === value).map(
            (o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ),
          )}
        </select>
      </label>
      <p className="max-w-[280px] text-right text-[11px] leading-snug text-text-secondary">
        {allowed
          ? 'A IA pensa antes de decidir: tende a acertar mais o caminho, custa mais por resposta e leva alguns segundos a mais.'
          : 'Sem efeito neste modelo: só modelos do Google pensam. Desligue para limpar a escolha.'}
      </p>
    </div>
  );
}

function isModelInProviders(model: string, providers: readonly ProviderModelsWire[]): boolean {
  return providers.some((p) => p.models.some((m) => m.id === model));
}

function AnthropicRecommendedBadge(): JSX.Element {
  return (
    <span
      title="Recomendamos fortemente modelos Anthropic para o agente de proteção"
      className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-800"
    >
      Recomendado: Anthropic
    </span>
  );
}

function CostBadge({ category }: { category: 'low' | 'medium' | 'high' }): JSX.Element {
  const styles = {
    low: 'bg-emerald-100 text-emerald-800',
    medium: 'bg-sky-100 text-sky-800',
    high: 'bg-rose-100 text-rose-800',
  } as const;
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${styles[category]}`}
    >
      {category}
    </span>
  );
}
